import { isAfter, isValid, parse } from 'date-fns';
import { DateRange } from 'react-day-picker';

/**
 * Format a Date object to DD/MM/YYYY string
 */
export function formatDate(date: Date | undefined): string {
  if (!date) {
    return '';
  }

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
}

/**
 * Check if a date string in DD/MM/YYYY format is valid
 */
export function isValidDate(value: string): boolean {
  // Parse the date in DD/MM/YYYY format
  const parsedDate = parse(value, 'dd/MM/yyyy', new Date());

  // Check if the parsed date is valid
  if (!isValid(parsedDate)) {
    return false;
  }

  return true;
}

/**
 * Validate that start date is not after end date (allows equal dates)
 */
export function validateDateRange(startDate: string, endDate: string): boolean {
  const startDateObj = parseDateToObject(startDate);
  const endDateObj = parseDateToObject(endDate);

  if (!startDateObj || !endDateObj) {
    return false;
  }

  // Start date should not be after end date (equal is OK)
  return !isAfter(startDateObj, endDateObj);
}

/**
 * Parse a DD/MM/YYYY string to a Date object
 */
export function parseDateToObject(value: string): Date | null {
  if (!isValidDate(value)) {
    return null;
  }

  return parse(value, 'dd/MM/yyyy', new Date());
}

/**
 * Auto-format user input to DD/MM/YYYY format as they type
 */
export function autoFormatDate(value: string, prevValue: string): string {
  const cleanValue = value.replaceAll(/\D/g, '');

  // Handle deletion - don't force slashes back in if user is deleting
  if (value.length < prevValue.length) {
    return value;
  }

  let formatted = cleanValue;
  if (cleanValue.length >= 2) {
    formatted = cleanValue.slice(0, 2) + '/' + cleanValue.slice(2);
  }
  if (cleanValue.length >= 4) {
    formatted = formatted.slice(0, 5) + '/' + cleanValue.slice(4, 8);
  }

  return formatted.slice(0, 10);
}

/**
 * Create a DateRange object from start and end date strings
 */
export function createDateRangeFromStrings(
  startDate: string,
  endDate: string,
): DateRange | undefined {
  const from = startDate.length === 10 ? parseDateToObject(startDate) : undefined;
  const to = endDate.length === 10 ? parseDateToObject(endDate) : undefined;

  if (!from && !to) {
    return undefined;
  }

  return {
    from: from ?? undefined,
    to: to ?? undefined,
  };
}

/**
 * Validate start date string and return error message if invalid
 */
export function getStartDateError(startDate: string): string | null {
  if (!startDate) {
    return null;
  }
  if (startDate.length < 10) {
    return null;
  }

  if (!isValidDate(startDate)) {
    return 'Invalid date';
  }

  return null;
}

/**
 * Validate end date string and return error message if invalid
 */
export function getEndDateError(startDate: string, endDate: string): string | null {
  if (!endDate) {
    return null;
  }
  if (endDate.length < 10) {
    return null;
  }

  // Check if end date itself is valid
  if (!isValidDate(endDate)) {
    return 'Invalid date';
  }

  // Check if end date is before start date
  if (startDate.length === 10 && endDate.length === 10) {
    if (!validateDateRange(startDate, endDate)) {
      return 'Invalid end date';
    }
  }

  return null;
}

/**
 * Format ISO date string to narrative timestamp format
 * Format: "10:30 AM . Jan 15"
 */
export function formatNarrativeTimestamp(isoString: string): string {
  if (!isoString) {
    return '';
  }

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, '0');
  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const month = monthNames[date.getMonth()];
  const day = date.getDate();

  return `${formattedHours}:${formattedMinutes} ${ampm} . ${month} ${day}`;
}

/**
 * Convert date string from DD/MM/YYYY format to YYYY-MM-DD format.
 * Validates the input format before conversion.
 * @param dateStr - Date string in DD/MM/YYYY format
 * @returns Date string in YYYY-MM-DD format, or original string if invalid
 */
export function convertDateFormat(dateStr: string): string {
  if (!dateStr) {
    return dateStr;
  }

  const parts = dateStr.split('/');
  if (parts.length !== 3) {
    return dateStr;
  }

  const [day, month, year] = parts;

  // Validate that all parts exist and match expected format
  if (!day || !month || !year) {
    return dateStr;
  }

  const dayIsValid = /^\d{2}$/.test(day);
  const monthIsValid = /^\d{2}$/.test(month);
  const yearIsValid = /^\d{4}$/.test(year);

  if (!dayIsValid || !monthIsValid || !yearIsValid) {
    return dateStr;
  }

  return `${year}-${month}-${day}`;
}

/**
 * Format date string (DD/MM/YYYY or ISO) to "20 Apr 2025" format
 * @param dateStr - Date string in DD/MM/YYYY or ISO format
 * @returns Formatted date string like "20 Apr 2025"
 */
export function formatDateDisplay(dateStr: string): string {
  if (!dateStr) {
    return '';
  }

  let date: Date;

  // Check if it's DD/MM/YYYY format
  if (dateStr.includes('/')) {
    const [day, month, year] = dateStr.split('/');
    date = new Date(Number(year), Number(month) - 1, Number(day));
  } else {
    // Assume ISO format
    date = new Date(dateStr);
  }

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year}`;
}

/**
 * Format date and time string (ISO) to "12 Dec 2025 . 09:02 AM" format
 * @param dateStr - Date string in ISO format
 * @returns Formatted date and time string like "12 Dec 2025 . 09:02 AM"
 */
export function formatDateWithTime(dateStr: string): string {
  if (!dateStr) {
    return '';
  }

  const date = new Date(dateStr);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const monthNames = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const day = date.getDate();
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();

  // Format time in 12-hour format
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHours = hours % 12 || 12;
  const formattedMinutes = minutes.toString().padStart(2, '0');

  return `${day} ${month} ${year} . ${formattedHours.toString().padStart(2, '0')}:${formattedMinutes} ${ampm}`;
}
